import * as THREE from 'three';

/**
 * FpsBuildEditMode — visual stub for Fortnite-style build editing.
 * Shows a 3×3 grid overlay over the targeted build piece.
 * Clicking a cell toggles it (red = marked for removal).
 * Press G or F to confirm (removes toggled cells visually).
 */
export class FpsBuildEditMode {
  private active = false;
  private targetBuild: THREE.Mesh | null = null;
  private gridOverlay!: THREE.Group;
  private cellMeshes: THREE.Mesh[] = [];
  private cellToggled: boolean[] = new Array(9).fill(false);
  private snapCellIdx = -1;
  private pulseT = 0;
  private readonly raycaster = new THREE.Raycaster();

  constructor(
    private scene: THREE.Scene,
    private buildPlacer: { hasRemovableBuildInLook: (c: THREE.Camera, d: number) => boolean },
  ) {
    this.gridOverlay = new THREE.Group();
    this.gridOverlay.visible = false;
    this.scene.add(this.gridOverlay);
    this.buildGrid();
  }

  private buildGrid(): void {
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        const geo = new THREE.PlaneGeometry(0.9, 0.9);
        const mat = new THREE.MeshBasicMaterial({
          color: 0x44aaff,
          transparent: true,
          opacity: 0.38,
          side: THREE.DoubleSide,
          depthWrite: false,
        });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.set((col - 1) * 1.0, (row - 1) * 1.0, 0.01);
        this.gridOverlay.add(mesh);
        this.cellMeshes.push(mesh);
      }
    }
  }

  activate(camera: THREE.Camera): boolean {
    this.raycaster.setFromCamera(new THREE.Vector2(0, 0), camera);
    this.raycaster.far = 5;
    const hits = this.raycaster.intersectObjects(this.scene.children, true);
    const hit = hits.find(
      (h) => h.object instanceof THREE.Mesh && (h.object as THREE.Mesh).userData.isPlayerBuild,
    );

    if (!hit) {
      this.active = false;
      this.gridOverlay.visible = false;
      return false;
    }

    this.targetBuild = hit.object as THREE.Mesh;
    this.active = true;

    // position overlay at the hit build
    this.gridOverlay.position.copy(this.targetBuild.position);
    this.gridOverlay.quaternion.copy(this.targetBuild.quaternion);
    this.gridOverlay.visible = true;

    // reset toggles
    this.cellToggled.fill(false);
    this.snapCellIdx = -1;
    this.pulseT = 0;
    this.refreshCellColors();
    return true;
  }

  deactivate(): void {
    this.active = false;
    this.gridOverlay.visible = false;
    this.targetBuild = null;
    this.cellToggled.fill(false);
    this.snapCellIdx = -1;
    for (const mesh of this.cellMeshes) {
      mesh.visible = true;
    }
    this.refreshCellColors();
  }

  isActive(): boolean {
    return this.active;
  }

  /** Toggle the cell under the crosshair (raycasts against grid meshes). */
  confirmEdit(camera: THREE.Camera): void {
    if (!this.active) return;

    // check if a cell is under cursor
    this.raycaster.setFromCamera(new THREE.Vector2(0, 0), camera);
    this.raycaster.far = 8;
    const hits = this.raycaster.intersectObjects(this.cellMeshes, false);
    if (hits.length > 0) {
      const idx = this.cellMeshes.indexOf(hits[0]!.object as THREE.Mesh);
      if (idx !== -1) {
        this.cellToggled[idx] = !this.cellToggled[idx];
        this.refreshCellColors();
        return;
      }
    }

    // confirm — hide toggled cells (visual only)
    for (let i = 0; i < this.cellMeshes.length; i++) {
      if (this.cellToggled[i]) {
        this.cellMeshes[i]!.visible = false;
      }
    }
    this.deactivate();
  }

  private refreshCellColors(now = 0): void {
    const pulse = 0.5 + 0.5 * Math.sin(now * 0.012);
    for (let i = 0; i < this.cellMeshes.length; i++) {
      const mat = this.cellMeshes[i]!.material as THREE.MeshBasicMaterial;
      if (this.cellToggled[i]) {
        mat.color.setHex(0xff4444);
        mat.opacity = 0.55 + pulse * 0.08;
      } else if (i === this.snapCellIdx) {
        mat.color.setHex(0x66f6ff);
        mat.opacity = 0.52 + pulse * 0.28;
      } else {
        mat.color.setHex(0x44aaff);
        mat.opacity = 0.34 + pulse * 0.06;
      }
    }
  }

  update(dt: number, camera: THREE.Camera): void {
    if (!this.active || !this.targetBuild) return;
    this.pulseT += dt;
    // keep overlay attached to target build
    this.gridOverlay.position.copy(this.targetBuild.position);
    this.gridOverlay.quaternion.copy(this.targetBuild.quaternion);
    // face camera
    const dir = new THREE.Vector3();
    camera.getWorldDirection(dir);
    const angle = Math.atan2(-dir.x, -dir.z);
    this.gridOverlay.rotation.y = angle;

    this.raycaster.setFromCamera(new THREE.Vector2(0, 0), camera);
    this.raycaster.far = 8;
    const hits = this.raycaster.intersectObjects(this.cellMeshes, false);
    const hitIdx = hits.length > 0 ? this.cellMeshes.indexOf(hits[0]!.object as THREE.Mesh) : -1;
    this.snapCellIdx = hitIdx;
    this.refreshCellColors(this.pulseT * 1000);
  }

  dispose(): void {
    this.gridOverlay.removeFromParent();
    for (const m of this.cellMeshes) {
      m.geometry.dispose();
      (m.material as THREE.MeshBasicMaterial).dispose();
    }
  }
}
